import {
	Box,
	Button,
	Checkbox,
	Chip,
	InputAdornment,
	Paper,
	Select,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import type { FieldConfig } from "../PropertyMappingSelector/PropertyMappingSelector.constants";
import {
	GlobalPropertyMappingSelectorProps,
	PropertyOption,
	PropertySortOption,
} from "./GlobalPropertyMappingSelector.types";
import { useGlobalPropertyMappingSelectorViewModel } from "./GlobalPropertyMappingSelector.vm";
import "./GlobalPropertyMappingSelector.css";

function PropertyCard({
	property,
	index,
	isSelected,
	onToggleSelect,
	multiDragCount = 0,
}: {
	property: PropertyOption;
	index: number;
	isSelected?: boolean;
	onToggleSelect?: (name: string, shiftKey: boolean) => void;
	multiDragCount?: number;
}) {
	return (
		<Draggable draggableId={property.name} index={index}>
			{(provided, snapshot) => {
				const isDimmed = !snapshot.isDragging && multiDragCount > 0;
				const showBadge = snapshot.isDragging && multiDragCount > 1;

				return (
					<Paper
						ref={provided.innerRef}
						{...provided.draggableProps}
						{...provided.dragHandleProps}
						elevation={snapshot.isDragging ? 4 : 1}
						onClick={(e) => {
							e.stopPropagation();
							onToggleSelect?.(property.name, e.shiftKey);
						}}
						sx={{
							p: 1,
							mb: 0.5,
							position: "relative",
							bgcolor: snapshot.isDragging ? "action.hover" : isSelected ? "primary.50" : "background.paper",
							cursor: "grab",
							"&:active": { cursor: "grabbing" },
							userSelect: "none",
							opacity: isDimmed ? 0.4 : 1,
							transition: "opacity 0.15s",
						}}
					>
						{showBadge && (
							<Chip
								label={multiDragCount}
								color="primary"
								size="small"
								sx={{
									position: "absolute",
									top: -8,
									right: -8,
									height: 22,
									minWidth: 22,
									fontWeight: 700,
									fontSize: "0.75rem",
									zIndex: 1,
								}}
							/>
						)}
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<Checkbox size="small" checked={!!isSelected} tabIndex={-1} disableRipple sx={{ p: 0, mr: 0.5 }} />
							<Typography variant="body2" fontWeight={700} noWrap>
								{property.name}
							</Typography>
						</Box>
						{property.examples?.slice(0, 3).map((ex, i) => (
							<Typography key={i} variant="caption" color="text.secondary" display="block" noWrap>
								{ex}
							</Typography>
						))}
					</Paper>
				);
			}}
		</Draggable>
	);
}

function JobPropertyCard({
	fieldConfig,
	assignedProperties,
	selectedProperties,
	multiDragCount,
}: {
	fieldConfig: FieldConfig;
	assignedProperties: PropertyOption[];
	selectedProperties: Set<string>;
	multiDragCount: number;
}) {
	return (
		<Paper variant="outlined" sx={{ p: 1.5, minHeight: 80 }}>
			<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
				{fieldConfig.label}
			</Typography>
			<Droppable droppableId={fieldConfig.key}>
				{(provided, snapshot) => (
					<Box
						ref={provided.innerRef}
						{...provided.droppableProps}
						sx={{
							minHeight: 40,
							borderRadius: 1,
							bgcolor: snapshot.isDraggingOver ? "primary.50" : "grey.50",
							border: snapshot.isDraggingOver ? "2px dashed" : "1px dashed",
							borderColor: snapshot.isDraggingOver ? "primary.main" : "grey.300",
							p: 0.5,
							transition: "all 0.15s",
						}}
					>
						{assignedProperties.length === 0 && !snapshot.isDraggingOver && (
							<Typography variant="caption" color="text.disabled" sx={{ display: "block", textAlign: "center", py: 1 }}>
								Drag properties here
							</Typography>
						)}
						{assignedProperties.map((property, index) => (
							<PropertyCard
								key={property.name}
								property={property}
								index={index}
								isSelected={selectedProperties.has(property.name)}
								multiDragCount={selectedProperties.has(property.name) ? multiDragCount : 0}
							/>
						))}
						{provided.placeholder}
					</Box>
				)}
			</Droppable>
		</Paper>
	);
}

export default function GlobalPropertyMappingSelector(props: GlobalPropertyMappingSelectorProps) {
	const vm = useGlobalPropertyMappingSelectorViewModel(props);

	if (!vm.shouldRender) {
		return null;
	}

	const showMoreLabel = (() => {
		if (vm.showMoreFields) return "Show Less";
		const withMappings =
			vm.hiddenFieldsWithMappingsCount > 0 ? ` (${vm.hiddenFieldsWithMappingsCount} with mappings)` : "";
		return `Show ${vm.hiddenFieldsCount} More${withMappings}`;
	})();

	return (
		<Box className="global-property-mapping-selector">
			<Box sx={{ mb: 2, flexShrink: 0 }} color="text.secondary">
				<Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
					Map properties from your uploaded files to the corresponding job fields. This will help us automatically
					populate the job details for you.
				</Typography>
			</Box>
			{vm.showAutoGuessNotice && (
				<Typography variant="body2" className="auto-guess-notice" sx={{ flexShrink: 0 }}>
					We&apos;ve automatically guessed some property mappings for you. Please review and adjust as needed.
				</Typography>
			)}

			<DragDropContext onBeforeCapture={vm.handleBeforeCapture} onDragEnd={vm.handleDragEnd}>
				<Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0 }}>
					{/* Left Panel — Unassigned Properties */}
					<Box sx={{ width: 280, flexShrink: 0, position: "relative" }}>
						<Box
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
								<Typography variant="subtitle2">Unassigned ({vm.unassignedProperties.length})</Typography>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									{vm.selectedProperties.size > 0 && (
										<Chip
											label={`${vm.selectedProperties.size} selected`}
											size="small"
											color="primary"
											onDelete={vm.clearSelection}
										/>
									)}
									<Select
										size="small"
										value={vm.sortOption}
										onChange={(e: SelectChangeEvent) => vm.setSortOption(e.target.value as PropertySortOption)}
										sx={{ fontSize: "0.75rem", height: 28 }}
									>
										<MenuItem value="name-asc">A → Z</MenuItem>
										<MenuItem value="name-desc">Z → A</MenuItem>
									</Select>
								</Box>
							</Box>

							<TextField
								size="small"
								placeholder="Filter properties..."
								value={vm.filterText}
								onChange={(e) => vm.setFilterText(e.target.value)}
								fullWidth
								sx={{ mb: 1 }}
								slotProps={{
									input: {
										endAdornment: vm.filterText ? (
											<InputAdornment position="end">
												<Chip
													label="Clear"
													size="small"
													onClick={() => vm.setFilterText("")}
													onDelete={() => vm.setFilterText("")}
													sx={{ height: 20, fontSize: "0.7rem" }}
												/>
											</InputAdornment>
										) : undefined,
									},
								}}
							/>

							<Droppable droppableId={vm.UNASSIGNED_DROPPABLE_ID}>
								{(provided, snapshot) => (
									<Paper
										ref={provided.innerRef}
										{...provided.droppableProps}
										variant="outlined"
										sx={{
											p: 1,
											flex: 1,
											minHeight: 0,
											overflowY: "auto",
											bgcolor: snapshot.isDraggingOver ? "action.hover" : "grey.50",
											transition: "background-color 0.15s",
										}}
									>
										{vm.unassignedProperties.length === 0 && (
											<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
												All properties assigned
											</Typography>
										)}
										{vm.unassignedProperties.length > 0 && vm.filteredUnassignedProperties.length === 0 && (
											<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
												No matches
											</Typography>
										)}
										{vm.filteredUnassignedProperties.map((property, index) => (
											<PropertyCard
												key={property.name}
												property={property}
												index={index}
												isSelected={vm.selectedProperties.has(property.name)}
												onToggleSelect={vm.handlePropertyClick}
												multiDragCount={vm.selectedProperties.has(property.name) ? vm.draggingSelectedCount : 0}
											/>
										))}
										{provided.placeholder}
									</Paper>
								)}
							</Droppable>
						</Box>
					</Box>

					{/* Right Panel — Job Property Cards */}
					<Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Job Properties ({vm.visibleFields.length})
						</Typography>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
								gap: 1.5,
							}}
						>
							{vm.visibleFields.map((field) => (
								<JobPropertyCard
									key={field.key}
									fieldConfig={field}
									assignedProperties={vm.selectedValues[field.key] || []}
									selectedProperties={vm.selectedProperties}
									multiDragCount={vm.draggingSelectedCount}
								/>
							))}
						</Box>
						{(vm.hiddenFieldsCount > 0 || vm.showMoreFields) && (
							<Button onClick={vm.toggleShowMore} variant="text" size="small" sx={{ mt: 1 }}>
								{showMoreLabel}
							</Button>
						)}
					</Box>
				</Box>
			</DragDropContext>
		</Box>
	);
}
